import React, { useEffect, useState } from 'react';

import { Button, Paper, Step, StepLabel, Stepper, Tooltip, Typography } from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import axios from 'axios';
import { FormikProvider, useFormik } from 'formik';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Link, useHistory } from 'react-router-dom';

import actionLibraryInstancesApi from 'config/api/actionLibraryInstance/actionLibraryInstance';
import {
  ActionLibraryInstanceCriticality,
  BulkCreateActionLibraryInstancePayload,
} from 'config/api/actionLibraryInstance/actionLibraryInstance.types';
import parseDate from 'helpers/parseDate';
import action_library_messages from 'messages/action_library_messages';
import PATHS from 'router/PATHS';

import ActionLibraryInstanceSummaryDialog from './_components/ActionLibraryInstanceSummaryDialog/ActionLibraryInstanceSummaryDialog';
import StepAssetSelection from './_components/Steps/StepAssetSelection';
import StepConfigureSteps from './_components/Steps/StepConfigureSteps';
import StepTemplateSelection from './_components/Steps/StepTemplateSelection';
import StepTimeParameters from './_components/Steps/StepTimeParameters';
import useStyles from './ActionLibraryInstanceForm.styles';
import { ActionLibraryInstanceFormValues } from './ActionLibraryInstanceForm.types';
import actionLibraryInstanceValidationSchema, {
  step1Schema,
  step2Schema,
  step3Schema,
  step4Schema,
} from './ActionLibraryInstanceForm.validation';

type Props = {
  initialValues?: ActionLibraryInstanceFormValues;
};

const steps = [
  action_library_messages.wizard.step_select_template,
  action_library_messages.wizard.step_time_parameters,
  action_library_messages.wizard.step_steps,
  action_library_messages.wizard.step_assets,
];

const ActionLibraryInstanceForm: React.FC<Props> = ({ initialValues }) => {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const styles = useStyles();
  const [activeStep, setActiveStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showSummaryDialog, setShowSummaryDialog] = useState(false);
  const [step3ValidationError, setStep3ValidationError] = useState<string | null>(null);
  const [step4ValidationError, setStep4ValidationError] = useState<string | null>(null);
  const [bulkCreateError, setBulkCreateError] = useState<string | null>(null);
  const [excludedAssetNamesInSummary, setExcludedAssetNamesInSummary] = useState<string | null>(null);

  const formik = useFormik<ActionLibraryInstanceFormValues>({
    initialValues: initialValues || {
      templateId: '',
      selectedTemplate: null,
      instanceName: '',
      reportedTo: '',
      criticality: '',
      deadlineDate: '',
      deadlineTime: '16:00',
      reminderDate: '',
      reminderTime: '16:00',
      validityPeriodMonths: null,
      specialInstruction: '',
      stepOverrides: [],
      assetIds: '',
      selectedAssets: [],
    },
    validationSchema: actionLibraryInstanceValidationSchema[activeStep],
    onSubmit: async () => {
      // This is called only when submitForm is called manually or on standard submit
      // For wizard, we handle it in handleNext/handleSave
    },
  });

  const { values, validateForm, setTouched, setFieldError, touched } = formik;

  // Clear "all assets already have active instances" banner when user changes selection in step 4
  useEffect(() => {
    if (activeStep === 3) {
      setBulkCreateError(null);
    }
  }, [activeStep, values.selectedAssets]);

  const handleSave = async () => {
    try {
      setLoading(true);

      const template = values.selectedTemplate;
      if (!template) throw new Error(t(action_library_messages.errors.no_template_selected));

      // Construct Adhoc Data: only included steps, order preserved, mandatory from override
      const includedOverrides = values.stepOverrides.filter(o => o.included);
      if (includedOverrides.length === 0) {
        enqueueSnackbar(t(action_library_messages.instance.validation.at_least_one_step_required), { variant: 'error' });
        return;
      }
      const adhocSteps = (template.steps || [])
        .filter(step => includedOverrides.some(o => o.originalStepId === step.id))
        .sort((a, b) => a.order - b.order)
        .map((step, index) => {
          const override = values.stepOverrides.find(o => o.originalStepId === step.id);
          const mandatory = override ? override.mandatory : !!step.mandatory;
          return {
            title: step.title,
            instruction: step.shortDescription,
            mandatory,
            order: index,
            reference: override?.reference || step.reference,
            relevant_for: override?.relevantFor || step.relevantFor,
          };
        });

      const deadlineDateStr = parseDate(values.deadlineDate);
      const reminderDateStr = parseDate(values.reminderDate);
      const reminder = reminderDateStr && values.reminderTime ? `${reminderDateStr}T${values.reminderTime}:00.000Z` : undefined;

      const assetIds = (values.selectedAssets || []).map(asset => Number(asset.id));
      const hasTemplateId = template.id != null;
      const templateStepOverrides =
        hasTemplateId && values.stepOverrides.filter(o => o.included).length > 0
          ? values.stepOverrides
              .filter(o => o.included)
              .map(o => ({
                stepId: o.originalStepId,
                mandatory: o.mandatory,
                ...(o.relevantFor != null && o.relevantFor !== '' && { relevantFor: o.relevantFor }),
                ...(o.reference != null && o.reference !== '' && { reference: o.reference }),
              }))
          : [];
      // TODO: Backend uses either template_id or adhoc_data; this split may change in the future.
      const payload: BulkCreateActionLibraryInstancePayload = {
        assetIds,
        ...(hasTemplateId && { templateId: Number(template.id) }),
        timeParameters: {
          deadline: `${deadlineDateStr}T${values.deadlineTime}:00.000Z`,
          ...(reminder != null && { reminder }),
        },
        ...(values.validityPeriodMonths != null && { validityPeriodMonths: values.validityPeriodMonths }),
        ...(values.criticality && { criticality: values.criticality as ActionLibraryInstanceCriticality }),
        ...(hasTemplateId && values.instanceName?.trim() !== '' && { instanceName: values.instanceName.trim() }),
        ...(hasTemplateId && values.reportedTo?.trim() !== '' && { reportedTo: values.reportedTo.trim() }),
        ...(hasTemplateId && values.specialInstruction?.trim() !== '' && { specialInstruction: values.specialInstruction.trim() }),
        ...(hasTemplateId && templateStepOverrides.length > 0 && { templateStepOverrides }),
        ...(!hasTemplateId && {
          adhocData: {
            title: values.instanceName,
            description: template.shortDescription || '',
            specialInstruction: values.specialInstruction,
            steps: adhocSteps,
          },
        }),
      };

      const response = await actionLibraryInstancesApi.createBulkActionLibraryInstances(payload);
      enqueueSnackbar(t(action_library_messages.success.instance_created), { variant: 'success' });
      const skippedHeader = response.headers['x-skipped-assets'] ?? response.headers['X-Skipped-Assets'];
      if (skippedHeader && String(skippedHeader).trim()) {
        enqueueSnackbar(
          t(action_library_messages.instance.bulk_create.skipped_assets_warning, { assetNames: String(skippedHeader).trim() }),
          { variant: 'warning' },
        );
      }
      history.push(PATHS.ACTION_LIBRARY_INSTANCE);
    } catch (err) {
      const data = axios.isAxiosError(err) ? (err.response?.data as { asset_ids?: string[] } | undefined) : undefined;
      if (axios.isAxiosError(err) && err.response?.status === 400 && data?.asset_ids) {
        const messages = Array.isArray(data.asset_ids) ? data.asset_ids : [String(data.asset_ids)];
        setBulkCreateError(messages.join(' '));
        return;
      }
      enqueueSnackbar(t(action_library_messages.errors.something_went_wrong_try_later), { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleNext = async () => {
    // Validate current step
    await validateForm();

    let currentStepSchema;
    if (activeStep === 0) currentStepSchema = step1Schema;
    else if (activeStep === 1) currentStepSchema = step2Schema;
    else if (activeStep === 2) currentStepSchema = step3Schema;
    else currentStepSchema = step4Schema;

    try {
      await currentStepSchema.validate(values, { abortEarly: false });
      if (activeStep === 2) {
        setFieldError('stepOverrides', undefined);
        setStep3ValidationError(null);
      }
      if (activeStep === 3) {
        setFieldError('selectedAssets', undefined);
        setStep4ValidationError(null);
        setBulkCreateError(null);
      }
      if (activeStep === steps.length - 1) {
        const template = values.selectedTemplate;
        const assetIds = (values.selectedAssets || []).map(a => Number(a.id));
        if (template?.id != null && assetIds.length > 0) {
          setLoading(true);
          try {
            const { data: precheckData } = await actionLibraryInstancesApi.createBulkPrecheck({
              templateId: Number(template.id),
              assetIds,
            });
            const alreadyActiveIds = new Set(precheckData.alreadyActiveAssetIds ?? []);
            if (alreadyActiveIds.size === assetIds.length) {
              setBulkCreateError(t(action_library_messages.instance.bulk_create.all_assets_active_error));
              return;
            }
            if (alreadyActiveIds.size > 0) {
              const names = (values.selectedAssets || [])
                .filter(a => alreadyActiveIds.has(Number(a.id)))
                .map(a => a.name)
                .join(', ');
              setExcludedAssetNamesInSummary(names);
            } else {
              setExcludedAssetNamesInSummary(null);
            }
          } catch {
            setExcludedAssetNamesInSummary(null);
          } finally {
            setLoading(false);
          }
        } else {
          setExcludedAssetNamesInSummary(null);
        }
        setShowSummaryDialog(true);
      } else {
        setActiveStep(prev => prev + 1);
        setTouched({});
      }
    } catch (err: any) {
      const touchedFields: any = {};
      if (err.inner?.length) {
        err.inner.forEach((error: any) => {
          touchedFields[error.path] = true;
        });
      }
      if (activeStep === 2) {
        touchedFields.stepOverrides = true;
        const needsAtLeastOneStep = err.inner?.some((e: { type?: string }) => e.type === 'at-least-one-included');
        const stepError = needsAtLeastOneStep
          ? t(action_library_messages.instance.validation.at_least_one_step_required)
          : err.inner?.[0]?.message ?? err.message ?? t(action_library_messages.instance.validation.at_least_one_step_required);
        setFieldError('stepOverrides', stepError);
        setStep3ValidationError(stepError);
      }
      if (activeStep === 3) {
        touchedFields.selectedAssets = true;
        const step4Error = t(action_library_messages.instance.validation.at_least_one_asset_required);
        setFieldError('selectedAssets', step4Error);
        setStep4ValidationError(step4Error);
      }
      setTouched({ ...touched, ...touchedFields });
    }
  };

  const handleBack = () => {
    if (activeStep === 2) setStep3ValidationError(null);
    if (activeStep === 3) {
      setStep4ValidationError(null);
      setBulkCreateError(null);
    }
    setActiveStep(prev => prev - 1);
  };

  const handleCloseSummaryDialog = () => {
    setShowSummaryDialog(false);
    setExcludedAssetNamesInSummary(null);
  };

  const handleConfirmSummary = async () => {
    setShowSummaryDialog(false);
    await handleSave();
  };

  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return <StepTemplateSelection />;
      case 1:
        return <StepTimeParameters />;
      case 2:
        return <StepConfigureSteps validationError={step3ValidationError} />;
      case 3:
        return <StepAssetSelection validationError={step4ValidationError} />;
      default:
        return t(action_library_messages.errors.unknown_step);
    }
  };

  const getButtonText = () => {
    if (activeStep === steps.length - 1) {
      return t(action_library_messages.wizard.review);
    }
    return t(action_library_messages.wizard.next);
  };

  return (
    <FormikProvider value={formik}>
      <div className={styles.root}>
        <Stepper activeStep={activeStep} alternativeLabel className={styles.stepper}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel>{t(label)}</StepLabel>
            </Step>
          ))}
        </Stepper>

        <Paper style={{ padding: 24 }}>
          <div className={styles.content}>{getStepContent(activeStep)}</div>

          {activeStep === 2 && step3ValidationError && (
            <div style={{ marginBottom: 16 }}>
              <Typography color='error' variant='body2'>
                {step3ValidationError}
              </Typography>
            </div>
          )}

          {activeStep === 3 && step4ValidationError && (
            <div style={{ marginBottom: 16 }}>
              <Typography color='error' variant='body2'>
                {step4ValidationError}
              </Typography>
            </div>
          )}

          {activeStep === 3 && bulkCreateError && (
            <Alert severity='error' style={{ marginBottom: 16 }}>
              {bulkCreateError}{' '}
              <Link rel='noopener noreferrer' style={{ whiteSpace: 'nowrap' }} target='_blank' to={PATHS.ACTION_LIBRARY_INSTANCE}>
                {t(action_library_messages.instance.bulk_create.view_existing_instances)}
              </Link>
            </Alert>
          )}

          <div className={styles.actions}>
            {activeStep > 0 && <Button onClick={handleBack}>{t(action_library_messages.wizard.back)}</Button>}

            {(activeStep > 0 || values.selectedTemplate) &&
              (() => {
                const isReviewDisabledByNoAssets = activeStep === 3 && (!values.selectedAssets || values.selectedAssets.length === 0);
                const isNextDisabledByNoIncludedSteps =
                  activeStep === 2 && values.stepOverrides.length > 0 && !values.stepOverrides.some(o => o.included);
                const isNextDisabled = loading || isReviewDisabledByNoAssets || isNextDisabledByNoIncludedSteps;
                let tooltipTitle = '';
                if (isReviewDisabledByNoAssets) {
                  tooltipTitle = t(action_library_messages.instance.validation.at_least_one_asset_required);
                } else if (isNextDisabledByNoIncludedSteps) {
                  tooltipTitle = t(action_library_messages.instance.validation.at_least_one_step_required);
                }
                const button = (
                  <span style={{ display: 'inline-block' }}>
                    <Button color='primary' disabled={isNextDisabled} onClick={handleNext} variant='contained'>
                      {getButtonText()}
                    </Button>
                  </span>
                );
                return <Tooltip title={tooltipTitle}>{button}</Tooltip>;
              })()}
          </div>
        </Paper>
      </div>

      <ActionLibraryInstanceSummaryDialog
        excludedAssetNames={excludedAssetNamesInSummary}
        isSubmitting={loading}
        onClose={handleCloseSummaryDialog}
        onConfirm={handleConfirmSummary}
        open={showSummaryDialog}
        values={values}
      />
    </FormikProvider>
  );
};

export default ActionLibraryInstanceForm;
